import { Badge } from '@art-of-hospitality-solutions-llc/ui-kit/components/badge';
import {
  Card,
  CardContent,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/card';
import { Separator } from '@art-of-hospitality-solutions-llc/ui-kit/components/separator';
import { formatCurrency } from '@Utils/utils';
import { getPaymentTypes } from '@Utils/toast/purchaseHelpers';
import { Check, ToastSelection, AppliedServiceCharge } from '@Types/toast';

type PurchaseReceiptProps = {
  check: Check;
  className?: string;
  showPaymentTypes?: boolean;
};

export default function PurchaseReceipt({
  check,
  className = '',
  showPaymentTypes = true,
}: PurchaseReceiptProps) {
  const selections = check.selections || [];
  const payments = check.payments || [];
  const serviceCharges = check.appliedServiceCharges || [];
  const discounts = check.appliedDiscounts || [];

  const subtotal = check.amount ?? 0;
  const totalAmount = check.totalAmount ?? 0;
  const taxAmount = check.taxAmount ?? 0;
  const tipAmount = payments.reduce((sum, p) => sum + (p.tipAmount || 0), 0);

  return (
    <Card className={className}>
      <CardContent>
        <div className="flex flex-col space-y-4">
          {selections.length > 0 && (
            <div className="space-y-2">
              {selections.map((selection: ToastSelection, index: number) => {
                const itemPrice =
                  selection.preDiscountPrice ?? selection.price ?? 0;
                return (
                  <div key={index} className="flex justify-between text-sm">
                    <span>
                      {selection.quantity}x {selection.displayName || 'Item'}
                    </span>
                    <span>{formatCurrency(itemPrice)}</span>
                  </div>
                );
              })}
            </div>
          )}

          <Separator />

          {/* Discounts */}
          {discounts.length > 0 && (
            <>
              {discounts.map((discount, index) => (
                <div key={index} className="flex justify-between text-sm">
                  <span className="text-zinc-500">
                    {discount.appliedPromoCode || discount.name || 'Discount'}
                    {discount.discountType === 'PERCENT' &&
                    discount.discountPercent
                      ? ` (${discount.discountPercent}%)`
                      : ''}
                  </span>
                  <span className="text-green-600">
                    -{formatCurrency(discount.discountAmount || 0)}
                  </span>
                </div>
              ))}

              <Separator />
            </>
          )}

          <div className="space-y-2">
            {/* Subtotal */}
            <div className="flex justify-between text-sm">
              <span className="text-zinc-500">Subtotal</span>
              <span>{formatCurrency(subtotal)}</span>
            </div>

            {/* Service Charges */}
            {serviceCharges.length > 0 &&
              serviceCharges.map(
                (charge: AppliedServiceCharge, index: number) => (
                  <div key={index} className="flex justify-between text-sm">
                    <span className="text-zinc-500">
                      {charge.name ||
                        charge.serviceCharge?.name ||
                        'Service Charge'}
                    </span>
                    <span>{formatCurrency(charge.chargeAmount ?? 0)}</span>
                  </div>
                ),
              )}

            {/* Tax */}
            {taxAmount > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-zinc-500">Tax</span>
                <span>{formatCurrency(taxAmount)}</span>
              </div>
            )}

            {/* Tip */}
            {tipAmount > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-zinc-500">Tip</span>
                <span>{formatCurrency(tipAmount)}</span>
              </div>
            )}

            {/* Total */}
            <div className="flex justify-between text-base font-semibold">
              <span>Total</span>
              <span>{formatCurrency(totalAmount)}</span>
            </div>
          </div>

          {showPaymentTypes && (
            <>
              <Separator />

              <div className="flex flex-row space-x-2">
                <div className="text-sm font-bold text-zinc-500">
                  Payment Type
                </div>
                <div className="flex flex-row gap-2">
                  {getPaymentTypes([check]).map((type) => (
                    <Badge key={`payment-type-${type}`}>{type}</Badge>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
